import type { FaqItem } from "@/lib/seo";

/** Global bilingual FAQ used on the home page (visible + FAQPage schema). */
export const homeFaq: { fr: FaqItem[]; en: FaqItem[] } = {
  fr: [
    {
      q: "Qu'est-ce que ReFound?",
      a: "ReFound est une application mobile canadienne qui transforme vos reçus papier en preuves d'achat intelligentes. Elle aide les consommateurs et les familles à scanner, enrichir, organiser, retrouver et partager leurs reçus pour les retours, les garanties et les achats du quotidien.",
    },
    {
      q: "À quoi sert une application de reçus?",
      a: "Une application de reçus aide à scanner, organiser et retrouver vos reçus. ReFound va plus loin en transformant vos reçus en preuves d'achat intelligentes, utiles pour les retours, les garanties et le partage familial.",
    },
    {
      q: "Qu'est-ce qu'un reçu intelligent?",
      a: "Un reçu intelligent est un reçu numérisé puis organisé pour être facilement retrouvé et utilisé. Il devient une preuve d'achat claire plutôt qu'une simple photo.",
    },
    {
      q: "Qu'est-ce qu'un reçu enrichi?",
      a: "Un reçu enrichi est un reçu auquel ReFound ajoute des informations utiles quand elles sont disponibles : nom de produit plus clair, image, catégorie, garantie du manufacturier ou date limite de retour estimée.",
    },
    {
      q: "Est-ce que la lecture automatique des reçus (OCR) est fiable?",
      a: "Oui. ReFound est propulsé par Google Vision, classé au sommet des benchmarks OCR indépendants (98 % de précision de texte), et par le modèle Gemini le plus avancé de Google pour les reçus effacés ou difficiles à lire. Marchand, date, total, taxes, carte de paiement et articles sont extraits automatiquement.",
    },
    {
      q: "Mes reçus sont-ils sauvegardés si je perds mon téléphone?",
      a: "Oui, lorsque la sauvegarde cloud est activée. Vos reçus restent privés et protégés, et sont conservés de façon sécurisée pour que vous puissiez les retrouver lorsque vous en avez besoin. Reconnectez-vous depuis un autre appareil et vos reçus sont là. La base de données principale de ReFound est hébergée au Canada.",
    },
    {
      q: "Est-ce que ReFound peut aider pour les retours en magasin?",
      a: "Oui. ReFound aide à retrouver et présenter le bon reçu, avec le code-barres quand disponible. L'acceptation d'un retour dépend toujours de la politique du marchand.",
    },
    {
      q: "Est-ce que ReFound peut afficher le code-barres d'un reçu?",
      a: "Quand un code-barres est détecté sur votre reçu, ReFound peut l'afficher à l'écran pour faciliter le passage au comptoir. La disponibilité dépend du reçu et du marchand.",
    },
    {
      q: "Est-ce que ReFound peut aider avec les garanties?",
      a: "Oui. ReFound aide à conserver vos reçus et, quand l'information est disponible, à associer la garantie du manufacturier, pour retrouver plus facilement votre preuve d'achat.",
    },
    {
      q: "Est-ce que je peux partager un reçu avec mon conjoint ou ma famille?",
      a: "Oui. ReFound permet de partager un reçu avec un conjoint ou un membre de la famille, par exemple quand l'un est au magasin et que la facture est sur le téléphone de l'autre.",
    },
    {
      q: "Est-ce que ReFound remplace le reçu papier?",
      a: "ReFound conserve une copie numérique lisible de vos reçus. Selon la politique du marchand, cette preuve d'achat peut suffire, mais certains marchands préfèrent le reçu original.",
    },
    {
      q: "Quels marchands sont supportés par ReFound?",
      a: "Tous! Vous pouvez scanner n'importe quel reçu de n'importe quel magasin : il est numérisé, organisé et utilisable pour un retour, avec le code-barres quand le reçu en a un. En plus, pour une liste de marchands populaires comme Canadian Tire, RONA, Home Depot, Walmart, Costco, Best Buy, Sports Experts, Décathlon, BMR, Canac, Bureau en Gros, Dollarama, Tigre Géant, Jean Coutu, Shoppers et Loblaws, ReFound enrichit les articles avec le vrai nom du produit, une photo, un lien et parfois la garantie lorsque disponible.",
    },
  ],
  en: [
    {
      q: "What is ReFound?",
      a: "ReFound is a Canadian mobile app that turns paper receipts into smart proof of purchase. It helps consumers and families scan, enrich, organize, find and share their receipts for returns, warranties and everyday purchases.",
    },
    {
      q: "What is a receipt app used for?",
      a: "A receipt app helps you scan, organize and find your receipts. ReFound goes further by turning your receipts into smart proof of purchase, useful for returns, warranties and family sharing.",
    },
    {
      q: "What is a smart receipt?",
      a: "A smart receipt is a digitized receipt organized so it is easy to find and use. It becomes clear proof of purchase rather than just a photo.",
    },
    {
      q: "What is an enriched receipt?",
      a: "An enriched receipt is a receipt where ReFound adds useful information when available: a clearer product name, image, category, manufacturer warranty or estimated return deadline.",
    },
    {
      q: "Is the automatic receipt reading (OCR) reliable?",
      a: "Yes. ReFound is powered by Google Vision, top-ranked in independent OCR benchmarks (98% text accuracy), plus Google's most advanced Gemini model for faded or hard-to-read receipts. Merchant, date, total, taxes, payment card and items are extracted automatically.",
    },
    {
      q: "Are my receipts backed up if I lose my phone?",
      a: "Yes, when cloud backup is on. Your receipts stay private and protected, and they're kept securely so you can find them when you need them. Sign in from another device and your receipts are there. ReFound's main database is hosted in Canada.",
    },
    {
      q: "Can ReFound help with in-store returns?",
      a: "Yes. ReFound helps you find and present the right receipt, with the barcode when available. Whether a return is accepted always depends on the merchant's policy.",
    },
    {
      q: "Can ReFound display a receipt barcode?",
      a: "When a barcode is detected on your receipt, ReFound can display it on screen to make the counter step easier. Availability depends on the receipt and the merchant.",
    },
    {
      q: "Can ReFound help track warranties?",
      a: "Yes. ReFound helps you keep your receipts and, when the information is available, attach the manufacturer warranty, so your proof of purchase is easier to find.",
    },
    {
      q: "Can I share a receipt with my partner or family?",
      a: "Yes. ReFound lets you share a receipt with a partner or family member, for example when one is at the store and the receipt is on the other's phone.",
    },
    {
      q: "Does ReFound replace paper receipts?",
      a: "ReFound keeps a readable digital copy of your receipts. Based on merchant policy, that proof of purchase may be enough, but some merchants prefer the original receipt.",
    },
    {
      q: "Which stores does ReFound support?",
      a: "All of them! You can scan any receipt from any store: it gets digitized, organized and ready to use for a return, with the barcode when the receipt has one. On top of that, for a list of popular merchants like Canadian Tire, RONA, Home Depot, Walmart, Costco, Best Buy, Sports Experts, Decathlon, BMR, Canac, Staples, Dollarama, Giant Tiger, Jean Coutu, Shoppers and Loblaws, ReFound enriches items with the real product name, a photo, a link and sometimes the warranty when available.",
    },
  ],
};
